/**
 * Outfit Admin API client.
 * Connects to /outfit_admin endpoints on the selected main API (apidev.polopan.com / apiv2.polopan.com).
 */

export const OUTFIT_ENV_STORAGE_KEY = 'outfit_target_api_env';

export const OUTFIT_ENV_URLS = {
  apidev: 'https://apidev.polopan.com',
  apiv2: 'https://apiv2.polopan.com',
};

export const getOutfitApiEnv = () => {
  return localStorage.getItem(OUTFIT_ENV_STORAGE_KEY) || 'apidev';
};

export const setOutfitApiEnv = (env) => {
  if (OUTFIT_ENV_URLS[env]) {
    localStorage.setItem(OUTFIT_ENV_STORAGE_KEY, env);
    window.dispatchEvent(new CustomEvent('outfit-env-changed', { detail: { env } }));
  }
};

export const getOutfitApiBaseUrl = () => {
  if (process.env.REACT_APP_OUTFIT_API_BASE_URL) {
    return process.env.REACT_APP_OUTFIT_API_BASE_URL.replace(/\/+$/, '');
  }
  const env = getOutfitApiEnv();
  return OUTFIT_ENV_URLS[env] || OUTFIT_ENV_URLS.apidev;
};

export const getAdminBaseUrl = getOutfitApiBaseUrl;

async function request(path, options = {}) {
  const baseUrl = getOutfitApiBaseUrl();
  const url = path.startsWith('http') ? path : `${baseUrl}${path}`;
  const authToken = localStorage.getItem('authToken') || localStorage.getItem('authKey') || '';
  const authKey = localStorage.getItem('authKey') || '';
  const userId = localStorage.getItem('userId') || '';
  const userEmail = localStorage.getItem('userEmail') || '';

  const headers = {
    Accept: 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    ...(authKey ? { 'X-Auth-Key': authKey } : {}),
    ...(userId ? { 'X-User-Id': userId } : {}),
    ...(userEmail ? { 'X-User-Email': userEmail } : {}),
    ...(options.headers || {}),
  };

  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers,
  });

  const contentType = res.headers.get('content-type') || '';
  let data;
  if (contentType.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const errorMsg =
      (data && data.detail) ||
      (data && data.message) ||
      (typeof data === 'string' ? data : res.statusText);
    const error = new Error(errorMsg || `Request failed with status ${res.status}`);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export const outfitAdminApi = {
  // ==========================================
  // 1. KEYWORD BLACKLIST ENDPOINTS
  // ==========================================

  /**
   * Fetch visual properties taxonomy and all active blacklisted combinations with live item counts.
   * GET /outfit_admin/blacklist_keyword/fetch
   */
  async fetchKeywordBlacklist() {
    return request('/outfit_admin/blacklist_keyword/fetch');
  },

  /**
   * Add a property combination to the keyword blacklist.
   * POST /outfit_admin/blacklist_keyword
   * @param {Object} payload { gender, super_category, category, terms_containing: string[], status: "add" }
   */
  async addKeywordBlacklist(payload) {
    return request('/outfit_admin/blacklist_keyword', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        status: 'add',
      }),
    });
  },

  /**
   * Remove a property combination from the keyword blacklist.
   * POST /outfit_admin/blacklist_keyword
   * @param {Object} payload { gender, super_category, category, terms_containing: string[], status: "remove" }
   */
  async removeKeywordBlacklist(payload) {
    return request('/outfit_admin/blacklist_keyword', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        status: 'remove',
      }),
    });
  },

  // ==========================================
  // 2. CCI BLACKLIST ENDPOINTS
  // ==========================================

  /**
   * Fetch all category combination documents with occasions and status.
   * GET /outfit_admin/blacklist_cci/fetch
   */
  async fetchCCIBlacklist() {
    return request('/outfit_admin/blacklist_cci/fetch');
  },

  /**
   * Update category combination status ('active' | 'discontinued').
   * POST /outfit_admin/blacklist_cci
   * @param {string} categoryCombinationId
   * @param {'active' | 'discontinued'} status
   */
  async updateCCIStatus(categoryCombinationId, status) {
    return request('/outfit_admin/blacklist_cci', {
      method: 'POST',
      body: JSON.stringify({
        category_combination_id: categoryCombinationId,
        status,
      }),
    });
  },

  // ==========================================
  // 3. OUTFIT TRENDS & CURATION ENDPOINTS
  // ==========================================

  /**
   * Fetch active category combinations for a gender.
   * GET /outfit_admin/trends/active_ccis?gender={gender}
   * @param {'men' | 'women'} gender
   */
  async fetchActiveCCIs(gender) {
    const query = new URLSearchParams({ gender });
    return request(`/outfit_admin/trends/active_ccis?${query.toString()}`);
  },

  /**
   * Fetch co-occurrence graph for a category combination ID.
   * GET /outfit_admin/trends/cooccurrence_graph?category_combination_id={categoryCombinationId}
   * @param {string} categoryCombinationId
   */
  async fetchCooccurrenceGraph(categoryCombinationId) {
    const query = new URLSearchParams({
      category_combination_id: categoryCombinationId,
    });
    return request(`/outfit_admin/trends/cooccurrence_graph?${query.toString()}`);
  },

  /**
   * Preview matched looks count for selected facets.
   * POST /outfit_admin/trends/preview_matched_count
   * @param {Object} payload { category_combination_id: string, selected_facets: Object }
   */
  async previewMatchedCount(payload) {
    return request('/outfit_admin/trends/preview_matched_count', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Create and publish a trend outfit collection.
   * POST /outfit_admin/trends/create_outfits
   * @param {Object} payload { name, gender, image, category_combination_id, selected_facets }
   */
  async createTrendOutfit(payload) {
    return request('/outfit_admin/trends/create_outfits', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Fetch summary of all saved trend collections.
   * GET /outfit_admin/trends/fetch?gender={gender}
   * @param {'men' | 'women'} [gender]
   */
  async fetchTrends(gender) {
    const query = new URLSearchParams();
    if (gender && gender !== 'all') {
      query.set('gender', gender);
    }
    const qs = query.toString();
    return request(`/outfit_admin/trends/fetch${qs ? `?${qs}` : ''}`);
  },

  /**
   * Fetch live hydrated product feed for a given trend ID.
   * GET /outfit_admin/trends/fetch/looks_by_trend_id?trend_id={trendId}&page={page}&limit={limit}
   * @param {string} trendId
   * @param {number} [page=1]
   * @param {number} [limit=20]
   */
  async fetchTrendLooks(trendId, page = 1, limit = 20) {
    const query = new URLSearchParams({
      trend_id: trendId,
      page: String(page),
      limit: String(limit),
    });
    return request(`/outfit_admin/trends/fetch/looks_by_trend_id?${query.toString()}`);
  },
};
